import { FC, useContext, useEffect, useState } from 'react';

import { ChangeBillingInfoForm } from '@pages/SubscriptionDetails/components/ChangeBillingInfoForm';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@global/components/ActiveForm/Elements/Checkbox';
import { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useOldBillingInfoSelector } from '@global/store/slices/changeSubscriptionPayment/selector';
import { toggleUseOldBillingInfo } from '@global/store/slices/changeSubscriptionPayment/changeSubscriptionPayment';
import { useAppDispatch, useAppSelector } from '@global/store/hooks';
import { subscriptionSelector } from '@global/store/slices/subscription/selectors';
import { buildStripeLocale } from '@global/utils/lang';
import { WpsPaymentForm } from '../../WpsPaymentForm';
import { ReloadInitResponseContext } from '@src/pages/checkout/components/SecurePayment';
import {
  PAYMENT_SUBMIT_BUTTON_ID,
  REACT_APP_WPS_DISABLE_CAPTCHA,
} from '@src/global/App/constants/constants';
import { WPSCustomEvent } from '../../WpsPaymentForm/types';
import {
  BillingFormType,
  BillingInfoFormValuesType,
} from '@global/components/BillingInfoForm/types';

interface SubscriptionCardProps {
  handleClose: (value: boolean) => void;
  billingInfoFormMethods: UseFormReturn<BillingInfoFormValuesType>;
  isShowCheckbox: boolean;
  subscriptionUid: string;
}

export const SubscriptionCard: FC<SubscriptionCardProps> = ({
  handleClose,
  billingInfoFormMethods,
  isShowCheckbox,
  subscriptionUid,
}) => {
  const { t } = useTranslation();
  const { paymentInitData, setPayment, setPaymentErr } = useContext(
    ReloadInitResponseContext
  );
  const { subscriptionsByUid } = useAppSelector(subscriptionSelector);
  const [isCartButtonLoading, setIsCartButtonLoading] =
    useState<boolean>(false);

  const locale = buildStripeLocale();

  const dispatch = useAppDispatch();
  const useOldBillingInfo = useSelector(useOldBillingInfoSelector);

  const billingInfo = subscriptionsByUid[subscriptionUid].billingInfo;

  useEffect(() => {
    if (useOldBillingInfo && billingInfo) {
      billingInfoFormMethods.setValue('country', billingInfo.country.iso2Code);
      billingInfoFormMethods.setValue('CCname', billingInfo.name);

      billingInfoFormMethods.setValue('address1', billingInfo.addressOne);
      billingInfoFormMethods.setValue('address2', billingInfo.addressTwo);

      billingInfoFormMethods.setValue('city', billingInfo.city);
      billingInfoFormMethods.setValue('zip', billingInfo.zip);
      billingInfoFormMethods.setValue('state', billingInfo.state);

      billingInfoFormMethods.setValue('phone', billingInfo.tel);
      billingInfoFormMethods.trigger();
    }
    if (!useOldBillingInfo) {
      billingInfoFormMethods.reset();
    }
  }, [useOldBillingInfo]);

  const handleOnSuccess = (
    wpsPaymentData: WPSCustomEvent['detail']['success']
  ) => {
    setPayment(wpsPaymentData);
  };

  const handleOnError = (err: WPSCustomEvent['detail']['error']) => {
    setIsCartButtonLoading(false);
    const errorMessage =
      err.returnMessage || t('checkout.step2.validationErr.unableProcess');
    setPaymentErr(errorMessage);
  };

  return (
    <>
      <div className="CardWrapper">
        <WpsPaymentForm
          onSuccess={handleOnSuccess}
          onError={handleOnError}
          appId={paymentInitData.appId}
          amount={paymentInitData.amount}
          currency={paymentInitData.currency}
          email={paymentInitData.email}
          intentType={paymentInitData.intentType}
          paymentReference={paymentInitData.paymentReference}
          paymentToken={paymentInitData.paymentToken}
          region={paymentInitData.region}
          timestamp={paymentInitData.timestamp}
          locale={locale}
          buttonId={PAYMENT_SUBMIT_BUTTON_ID}
          disableCaptcha={REACT_APP_WPS_DISABLE_CAPTCHA}
        />
      </div>
      <div>
        <div className="billing-info payment-info-form-wrapper">
          <span className="billing-info__subtitle regularText">
            {t('checkout.step2.subTitle')}
          </span>
          {isShowCheckbox ? (
            <div className="subscription_details_checkbox">
              <Checkbox
                isHidden={false}
                isChecked={useOldBillingInfo}
                toggle={() => {
                  dispatch(toggleUseOldBillingInfo());
                }}
                text={t('subDetails.sub.billing.billingEditCta.checkbox')}
              />
            </div>
          ) : null}

          <ChangeBillingInfoForm
            formType={BillingFormType.ChangePaymentForm}
            methods={billingInfoFormMethods}
            buttonId={'btn-payment-element-submit'}
            buttonText={t('subDetails.sub.billing.paymentEditCta.cta')}
            setIsShow={handleClose}
            isShow={true}
            withPaymentForm
            isCartButtonLoading={isCartButtonLoading}
            setIsCartButtonLoading={setIsCartButtonLoading}
            billingInfo={useOldBillingInfo ? billingInfo : null}
            setPaymentErr={setPaymentErr}
          />
        </div>
      </div>
    </>
  );
};
